// Comparesrt.js
import { useState, useEffect, useRef, useCallback } from "react";
import srtParser2 from "srt-parser-2";
import "./Comparesrt.css";

const Comparesrt = () => {
  const [subOneItems, setSubOneItems] = useState([]);
  const [subTwoItems, setSubTwoItems] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedSide, setSelectedSide] = useState(null);
  const [syncScroll, setSyncScroll] = useState(true);
  const [autoScroll, setAutoScroll] = useState(true);

  const leftColumnRef = useRef(null);
  const rightColumnRef = useRef(null);
  const [syncingScroll, setSyncingScroll] = useState(false);

  // پارس کردن فایل زیرنویس
  const parseSubtitle = (number, file) => {
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = () => {
      const text = reader.result;
      let parser = new srtParser2();
      let result = parser.fromSrt(text);

      // مرتب‌سازی بر اساس زمان شروع
      result.sort((a, b) => a.startSeconds - b.startSeconds);

      if (number === "one") setSubOneItems(result);
      if (number === "two") setSubTwoItems(result);
    };
  };

  // محاسبه شدت رنگ بر اساس نزدیکی زمانی
  const getHighlightIntensity = (itemTime, selectedTime) => {
    if (!selectedTime || !itemTime) return 0;

    const itemMidpoint = (itemTime.startSeconds + itemTime.endSeconds) / 2;
    const selectedMidpoint =
      (selectedTime.startSeconds + selectedTime.endSeconds) / 2;
    const distance = Math.abs(itemMidpoint - selectedMidpoint);

    // حداکثر فاصله زمانی برای هایلایت (3 ثانیه)
    const maxDistance = 3.0;

    if (distance > maxDistance) return 0;

    // محاسبه intensity از 0 تا 1
    const intensity = Math.max(0, 1 - distance / maxDistance);

    // اگر دقیقاً همپوشانی دارند
    if (
      itemTime.startSeconds === selectedTime.startSeconds &&
      itemTime.endSeconds === selectedTime.endSeconds
    ) {
      return 1;
    }

    // استفاده از تابع easing برای تغییرات نرم‌تر
    return Math.pow(intensity, 1.2);
  };

  // کلیک روی یک آیتم زیرنویس
  const handleItemClick = (item, side) => {
    setSelectedTime({
      startSeconds: item.startSeconds,
      endSeconds: item.endSeconds,
      id: item.id,
    });
    setSelectedSide(side);

    // اسکرول خودکار اگر فعال باشد
    if (autoScroll) {
      setTimeout(() => {
        scrollToNearestMatch(item, side);
      }, 100);
    }
  };

  // اسکرول به نزدیکترین تطابق در ستون مقابل
  const scrollToNearestMatch = (selectedItem, selectedSide) => {
    const targetItems = selectedSide === "left" ? subTwoItems : subOneItems;
    const targetRef = selectedSide === "left" ? rightColumnRef : leftColumnRef;

    if (!targetItems.length || !targetRef.current) return;

    // پیدا کردن نزدیکترین آیتم
    const selectedMidpoint =
      (selectedItem.startSeconds + selectedItem.endSeconds) / 2;
    let nearestItem = targetItems[0];
    let minDistance = Math.abs(
      (targetItems[0].startSeconds + targetItems[0].endSeconds) / 2 -
        selectedMidpoint,
    );

    targetItems.forEach((item) => {
      const itemMidpoint = (item.startSeconds + item.endSeconds) / 2;
      const distance = Math.abs(itemMidpoint - selectedMidpoint);
      if (distance < minDistance) {
        minDistance = distance;
        nearestItem = item;
      }
    });

    // پیدا کردن المنت مربوطه و اسکرول به آن
    const targetElement = targetRef.current.querySelector(
      `[data-sub-id="${nearestItem.id}"]`,
    );
    if (targetElement) {
      targetElement.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  // سینک کردن اسکرول
  const handleScroll = useCallback(
    (sourceRef, targetRef) => {
      if (!syncScroll || syncingScroll) return;

      setSyncingScroll(true);
      const source = sourceRef.current;
      const target = targetRef.current;

      if (source && target) {
        const scrollPercentage =
          source.scrollTop / (source.scrollHeight - source.clientHeight);
        target.scrollTop =
          scrollPercentage * (target.scrollHeight - target.clientHeight);
      }

      requestAnimationFrame(() => {
        setSyncingScroll(false);
      });
    },
    [syncScroll, syncingScroll],
  );

  // رندر یک آیتم زیرنویس
  const SubtitleItem = ({ item, side }) => {
    // محاسبه intensity فقط برای آیتم‌های سمت مقابل
    let intensity = 0;
    if (selectedTime && selectedSide && selectedSide !== side) {
      intensity = getHighlightIntensity(item, selectedTime);
    }

    const isSelected = selectedTime?.id === item.id && selectedSide === side;
    const isExactMatch = intensity === 1;
    const isPartialMatch = intensity > 0 && intensity < 1;

    // تعیین کلاس‌های CSS
    const itemClasses = [
      "comparesrt-item",
      isSelected ? "selected" : "",
      isExactMatch ? "exact-match" : "",
      isPartialMatch ? "partial-match" : "",
    ]
      .filter(Boolean)
      .join(" ");

    // استایل‌های inline برای موارد dynamic
    let dynamicStyles = {};

    if (isPartialMatch) {
      const alpha = 0.08 + intensity * 0.17; // 0.08 تا 0.25
      dynamicStyles.backgroundColor = `rgba(59, 130, 246, ${alpha})`;

      // border color با شدت متغیر
      const blue = Math.round(59 + (147 - 59) * intensity);
      const green = Math.round(130 + (197 - 130) * intensity);
      const red = Math.round(246 + (51 - 246) * intensity);
      dynamicStyles.borderColor = `rgb(${red}, ${green}, ${blue})`;
      dynamicStyles.borderWidth = "1.5px";
    }

    // استایل time badge برای آیتم‌های هایلایت شده
    const timeBadgeClasses = [
      "comparesrt-item-time-badge",
      isExactMatch || isPartialMatch ? "highlighted" : "",
    ]
      .filter(Boolean)
      .join(" ");

    const timeBadgeStyle = {};
    if (isExactMatch || isPartialMatch) {
      timeBadgeStyle.backgroundColor = `rgba(59, 130, 246, ${0.05 + intensity * 0.1})`;
    }

    return (
      <div
        data-sub-id={item.id}
        onClick={() => handleItemClick(item, side)}
        className={itemClasses}
        style={dynamicStyles}
      >
        {/* Time Row */}
        <div className="comparesrt-item-time-row">
          <span className={timeBadgeClasses} style={timeBadgeStyle}>
            {item.startTime} → {item.endTime}
          </span>

          {(isExactMatch || isPartialMatch) && (
            <span
              className={`comparesrt-item-match-badge ${isExactMatch ? "exact" : "partial"}`}
            >
              {isExactMatch
                ? "✓ تطابق دقیق"
                : `~${Math.round(intensity * 100)}% تطابق`}
            </span>
          )}
        </div>

        {/* Text Content */}
        <div
          className={`comparesrt-item-text ${side === "right" ? "right" : "left"}`}
        >
          {item.text}
        </div>
      </div>
    );
  };

  return (
    <div className="comparesrt-container">
      {/* Header */}
      <div className="comparesrt-header">
        <h1 className="comparesrt-title">📝 مقایسه و سینک زیرنویس‌ها</h1>
        <div className="comparesrt-upload-group">
          <div className="comparesrt-upload-item">
            <label className="comparesrt-upload-label">
              زیرنویس مرجع (انگلیسی)
            </label>
            <input
              type="file"
              accept=".srt"
              onChange={(e) =>
                e.target.files[0] && parseSubtitle("one", e.target.files[0])
              }
              className="comparesrt-upload-input"
            />
          </div>
          <div className="comparesrt-upload-item">
            <label className="comparesrt-upload-label">
              زیرنویس دوم (فارسی)
            </label>
            <input
              type="file"
              accept=".srt"
              onChange={(e) =>
                e.target.files[0] && parseSubtitle("two", e.target.files[0])
              }
              className="comparesrt-upload-input"
            />
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="comparesrt-controls">
        <h3 className="comparesrt-controls-title">⚙️ تنظیمات نمایش:</h3>

        <div className="comparesrt-checkbox-group">
          <input
            type="checkbox"
            id="syncScroll"
            checked={syncScroll}
            onChange={(e) => setSyncScroll(e.target.checked)}
            className="comparesrt-checkbox"
          />
          <label htmlFor="syncScroll" className="comparesrt-checkbox-label">
            اسکرول هماهنگ دو ستون
          </label>
        </div>

        <div className="comparesrt-divider"></div>

        <div className="comparesrt-checkbox-group">
          <input
            type="checkbox"
            id="autoScroll"
            checked={autoScroll}
            onChange={(e) => setAutoScroll(e.target.checked)}
            className="comparesrt-checkbox"
          />
          <label htmlFor="autoScroll" className="comparesrt-checkbox-label">
            اسکرول خودکار به زیرنویس متناظر
          </label>
        </div>
      </div>

      {/* Legend */}
      <div className="comparesrt-legend">
        <span className="comparesrt-legend-title">🎨 راهنما:</span>

        <div className="comparesrt-legend-item">
          <div className="comparesrt-legend-color selected"></div>
          <span className="comparesrt-legend-text">انتخاب شده</span>
        </div>

        <div className="comparesrt-legend-item">
          <div className="comparesrt-legend-color exact"></div>
          <span className="comparesrt-legend-text">تطابق دقیق</span>
        </div>

        <div className="comparesrt-legend-item">
          <div className="comparesrt-legend-color partial"></div>
          <span className="comparesrt-legend-text">تطابق نسبی</span>
        </div>

        <div className="comparesrt-legend-item">
          <div className="comparesrt-legend-color none"></div>
          <span className="comparesrt-legend-text">بدون تطابق</span>
        </div>
      </div>

      {/* Subtitle Columns */}
      <div className="comparesrt-columns">
        {/* Left Column */}
        <div
          ref={leftColumnRef}
          onScroll={() => handleScroll(leftColumnRef, rightColumnRef)}
          className="comparesrt-column"
        >
          <div className="comparesrt-column-header">
            <h3 className="comparesrt-column-title">
              🇬🇧 زیرنویس مرجع ({subOneItems.length} دیالوگ)
            </h3>
          </div>
          {subOneItems.length === 0 ? (
            <div className="comparesrt-empty-message">
              لطفاً فایل زیرنویس را بارگذاری کنید
            </div>
          ) : (
            subOneItems.map((item, index) => (
              <SubtitleItem
                key={`${item.id}-${index}`}
                item={item}
                side="left"
              />
            ))
          )}
        </div>

        {/* Right Column */}
        <div
          ref={rightColumnRef}
          onScroll={() => handleScroll(rightColumnRef, leftColumnRef)}
          className="comparesrt-column"
        >
          <div className="comparesrt-column-header">
            <h3 className="comparesrt-column-title">
              🇮🇷 زیرنویس دوم ({subTwoItems.length} دیالوگ)
            </h3>
          </div>
          {subTwoItems.length === 0 ? (
            <div className="comparesrt-empty-message">
              لطفاً فایل زیرنویس را بارگذاری کنید
            </div>
          ) : (
            subTwoItems.map((item, index) => (
              <SubtitleItem
                key={`${item.id}-${index}`}
                item={item}
                side="right"
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default Comparesrt;
